import { useState, useEffect } from "react";
import { Sun, Moon, Zap, Plus, X } from "lucide-react";
import { dbGet, dbSet } from "../lib/db";

const SK  = "reconectados_journal";
const MK  = "reconectados_morning";
const MOK = "reconectados_moments";

const MOODS = ["😔", "😕", "😐", "🙂", "😊", "😄"];
const TRIGGERS = [
  "Estrés", "Soledad", "Aburrimiento", "Ansiedad", "Tristeza",
  "Conflicto familiar", "Trabajo", "Problemas económicos",
  "Celebración", "Dolor físico", "Insomnio", "Recuerdos",
];
const MOMENT_TYPES = [
  { id: "ansioso",    emoji: "😤", label: "Ansioso" },
  { id: "bien",       emoji: "😊", label: "Me sentí bien" },
  { id: "craving",    emoji: "😰", label: "Craving" },
  { id: "supere",     emoji: "💪", label: "Lo superé" },
  { id: "bajon",      emoji: "😔", label: "Bajón" },
  { id: "euforia",    emoji: "⚡", label: "Euforia" },
  { id: "enojo",      emoji: "😠", label: "Enojo" },
  { id: "calma",      emoji: "😌", label: "Momento de calma" },
  { id: "ayuda",      emoji: "🤝", label: "Pedí ayuda" },
  { id: "medicacion", emoji: "💊", label: "Medicación extra" },
];
const WAKE_STATES = [
  { id: "bien",    label: "Bien" },
  { id: "regular", label: "Regular" },
  { id: "mal",     label: "Mal" },
];

function riskColor(v) { return v <= 3 ? "#84A98C" : v <= 6 ? "#F4A261" : "#E07A5F"; }
function formatTime(iso) {
  return new Date(iso).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
}
function isoDate(d) { return d.toISOString().slice(0, 10); }

export default function DayEditor({ dayKey, onClose, onSaved }) {
  const dateLabel = new Date(dayKey + "T12:00:00").toLocaleDateString("es-ES", {
    weekday: "long", day: "numeric", month: "long",
  });

  const [ready, setReady] = useState(false);

  // Morning
  const [existingMorning, setExistingMorning] = useState(null);
  const [wakeState,   setWakeState]   = useState("bien");
  const [sueno,       setSueno]       = useState(7);
  const [medicacion,  setMedicacion]  = useState(false);
  const [impulso,     setImpulso]     = useState(1);
  const [showMorning, setShowMorning] = useState(false);

  // Evening
  const [existingEvening, setExistingEvening] = useState(null);
  const [mood,        setMood]        = useState(3);
  const [riskLevel,   setRiskLevel]   = useState(3);
  const [ansiedad,    setAnsiedad]    = useState(2);
  const [consumo,     setConsumo]     = useState(false);
  const [selTriggers, setSelTriggers] = useState([]);
  const [notes,       setNotes]       = useState("");
  const [showEvening, setShowEvening] = useState(false);

  // Moments
  const [moments,        setMoments]        = useState([]);
  const [showMomentForm, setShowMomentForm] = useState(false);
  const [selMoment,      setSelMoment]      = useState(null);
  const [momentNote,     setMomentNote]     = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [morningMap, eveningList, allMoments] = await Promise.all([
          dbGet(MK, {}),
          dbGet(SK, []),
          dbGet(MOK, []),
        ]);
        if (cancelled) return;

        const safeMap  = morningMap && typeof morningMap === "object" ? morningMap : {};
        const safeList = Array.isArray(eveningList) ? eveningList : [];
        const safeMoms = Array.isArray(allMoments)  ? allMoments  : [];

        const em = safeMap[dayKey] ?? null;
        const ee = safeList.find(e => e?.date && e.date.slice(0, 10) === dayKey) ?? null;
        const dm = safeMoms.filter(m => m?.time && m.time.slice(0, 10) === dayKey);

        setExistingMorning(em);
        if (em) {
          setWakeState(em.wakeState ?? "bien");
          setSueno(em.sueno ?? 7);
          setMedicacion(em.medicacion ?? false);
          setImpulso(em.impulso ?? 1);
        }

        setExistingEvening(ee);
        if (ee) {
          setMood(ee.mood ?? 3);
          setRiskLevel(ee.riskLevel ?? 3);
          setAnsiedad(ee.ansiedad ?? 2);
          setConsumo(ee.consumo ?? false);
          setSelTriggers(ee.triggers ?? []);
          setNotes(ee.notes ?? "");
        }

        setMoments(dm);
      } catch (err) {
        console.error("DayEditor load error:", err);
      } finally {
        if (!cancelled) setReady(true);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [dayKey]);

  const toggleTrigger = t =>
    setSelTriggers(p => p.includes(t) ? p.filter(x => x !== t) : [...p, t]);

  const saveMorning = async () => {
    const data = { wakeState, sueno, medicacion, impulso, savedAt: new Date().toISOString() };
    const all  = await dbGet(MK, {});
    all[dayKey] = data;
    await dbSet(MK, all);
    setExistingMorning(data);
    setShowMorning(false);
    onSaved?.();
  };

  const saveEvening = async () => {
    const entry = {
      id:   existingEvening?.id ?? Date.now().toString(),
      // preserve original date if editing, otherwise use noon of the selected day
      date: existingEvening?.date ?? (dayKey + "T12:00:00.000Z"),
      mood, riskLevel, ansiedad, consumo,
      triggers: selTriggers,
      notes,
    };
    // replace any existing entry for this day (dedup by date key)
    const raw  = await dbGet(SK, []);
    const list = (Array.isArray(raw) ? raw : []).filter(e => e?.date && e.date.slice(0, 10) !== dayKey);
    list.push(entry);
    await dbSet(SK, list);
    setExistingEvening(entry);

    // update streak only when saving today
    const todayKey = isoDate(new Date());
    if (dayKey === todayKey) {
      const today    = new Date().toDateString();
      const lastDate = await dbGet("reconectados_last_entry", null);
      const streak   = await dbGet("reconectados_streak", 0);
      if (lastDate !== today) {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const newStreak = lastDate === yesterday.toDateString() ? streak + 1 : 1;
        await dbSet("reconectados_streak", newStreak);
        await dbSet("reconectados_last_entry", today);
      }
    }
    setShowEvening(false);
    onSaved?.();
  };

  const addMoment = async () => {
    if (!selMoment) return;
    const m = {
      ...selMoment,
      note: momentNote,
      time: dayKey + "T" + new Date().toTimeString().slice(0, 8) + ".000Z",
    };
    const all = await dbGet(MOK, []);
    all.push(m);
    await dbSet(MOK, all);
    setMoments(prev => [...prev, m]);
    setSelMoment(null);
    setMomentNote("");
    setShowMomentForm(false);
    onSaved?.();
  };

  const deleteMoment = async (idx) => {
    const all     = await dbGet(MOK, []);
    const updated = all.filter(m => m.time.slice(0, 10) !== dayKey);
    const keep    = moments.filter((_, i) => i !== idx);
    keep.forEach(m => updated.push(m));
    await dbSet(MOK, updated);
    setMoments(keep);
    onSaved?.();
  };

  if (!ready) return (
    <div className="bg-white rounded-2xl border border-[#E4EBE6] mb-4 p-6 flex items-center justify-center">
      <div className="w-5 h-5 rounded-full border-2 border-[#6B9080] border-t-transparent animate-spin" />
    </div>
  );

  return (
    <div className="bg-white rounded-2xl border border-[#E4EBE6] mb-4 overflow-hidden animate-fade-in-up">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#F0F5F2]">
        <p className="text-sm font-semibold text-[#1E2F23] capitalize" style={{ fontFamily: "Manrope" }}>
          {dateLabel}
        </p>
        {onClose && (
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-[#F7F9F7] flex items-center justify-center">
            <X size={16} className="text-[#2C4238]" />
          </button>
        )}
      </div>

      <div className="px-5 py-4 space-y-4">

        {/* ── Morning ── */}
        <div className="border border-[#E4EBE6] rounded-2xl overflow-hidden">
          <button
            onClick={() => setShowMorning(o => !o)}
            className="w-full flex items-center justify-between px-4 py-3"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-[#FFF9EE] flex items-center justify-center">
                <Sun size={15} className="text-[#F4A261]" />
              </div>
              <div className="text-left">
                <p className="text-xs font-semibold text-[#1E2F23]">Check-in matutino</p>
                {existingMorning
                  ? <p className="text-xs text-[#84A98C]">Completado · {existingMorning.sueno}h sueño</p>
                  : <p className="text-xs text-[#F4A261]">Sin registro</p>
                }
              </div>
            </div>
            <span className="text-xs font-semibold text-[#6B9080]">
              {showMorning ? "Cerrar" : existingMorning ? "Editar" : "Añadir"}
            </span>
          </button>

          {showMorning && (
            <div className="px-4 pb-4 border-t border-[#F0F5F2] animate-fade-in space-y-4 pt-4">
              <div>
                <p className="text-xs font-semibold text-[#2C4238] uppercase tracking-wide mb-2">¿Cómo amaneciste?</p>
                <div className="flex gap-2">
                  {WAKE_STATES.map(w => (
                    <button key={w.id} onClick={() => setWakeState(w.id)}
                      className={`flex-1 py-2 rounded-xl text-xs font-semibold border-2 transition-all ${
                        wakeState === w.id ? "bg-[#6B9080] text-white border-[#6B9080]" : "bg-white text-[#2C4238] border-[#E4EBE6]"
                      }`}>
                      {w.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <p className="text-xs font-semibold text-[#2C4238] uppercase tracking-wide">Horas de sueño</p>
                  <span className="text-sm font-bold text-[#1E2F23]">{sueno}h</span>
                </div>
                <input type="range" min="2" max="12" step="0.5" value={sueno}
                  onChange={e => setSueno(parseFloat(e.target.value))} className="w-full accent-[#6B9080]" />
              </div>
              <div className="flex items-center justify-between bg-[#F7F9F7] rounded-xl px-4 py-3">
                <p className="text-xs font-semibold text-[#2C4238]">¿Tomé mi medicación?</p>
                <button onClick={() => setMedicacion(m => !m)}
                  className={`w-12 h-6 rounded-full transition-colors relative ${medicacion ? "bg-[#6B9080]" : "bg-[#E4EBE6]"}`}>
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${medicacion ? "translate-x-7" : "translate-x-1"}`} />
                </button>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <p className="text-xs font-semibold text-[#2C4238] uppercase tracking-wide">Impulso de consumo (1-5)</p>
                  <span className={`text-sm font-bold ${impulso <= 2 ? "text-[#84A98C]" : impulso <= 3 ? "text-[#F4A261]" : "text-[#E07A5F]"}`}>{impulso}</span>
                </div>
                <input type="range" min="1" max="5" value={impulso}
                  onChange={e => setImpulso(Number(e.target.value))} className="w-full accent-[#6B9080]" />
              </div>
              <button onClick={saveMorning} className="w-full py-3 rounded-2xl bg-[#6B9080] text-white font-semibold text-sm">
                Guardar mañana
              </button>
            </div>
          )}
        </div>

        {/* ── Moments ── */}
        <div className="border border-[#E4EBE6] rounded-2xl overflow-hidden">
          <button
            onClick={() => setShowMomentForm(o => !o)}
            className="w-full flex items-center justify-between px-4 py-3"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-[#EDF4FF] flex items-center justify-center">
                <Zap size={15} className="text-[#5B8FCC]" />
              </div>
              <div className="text-left">
                <p className="text-xs font-semibold text-[#1E2F23]">Momentos del día</p>
                <p className="text-xs text-[#2C4238]">
                  {moments.length} registrado{moments.length !== 1 ? "s" : ""}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-[#5B8FCC]">
                {showMomentForm ? "Cerrar" : "Añadir"}
              </span>
              <Plus size={14} className="text-[#4A6E5C]" />
            </div>
          </button>

          {moments.length > 0 && (
            <div className="px-4 pb-3 space-y-2 border-t border-[#F0F5F2] pt-3">
              {moments.map((m, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-base leading-none mt-0.5">{m.emoji}</span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-medium text-[#1E2F23]">{m.label}</p>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-[#4A6E5C]">{formatTime(m.time)}</p>
                        <button onClick={() => deleteMoment(i)} className="text-[#E07A5F] opacity-60 hover:opacity-100">
                          <X size={12} />
                        </button>
                      </div>
                    </div>
                    {m.note && <p className="text-xs text-[#2C4238] mt-0.5">{m.note}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {showMomentForm && (
            <div className="px-4 pb-4 border-t border-[#F0F5F2] animate-fade-in pt-3 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {MOMENT_TYPES.map(mt => (
                  <button key={mt.id} onClick={() => setSelMoment(mt)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 text-xs font-medium transition-all ${
                      selMoment?.id === mt.id
                        ? "border-[#6B9080] bg-[#EEF4F1] text-[#1E2F23]"
                        : "border-[#E4EBE6] text-[#2C4238]"
                    }`}>
                    <span className="text-base">{mt.emoji}</span> {mt.label}
                  </button>
                ))}
              </div>
              <textarea value={momentNote} onChange={e => setMomentNote(e.target.value)}
                placeholder="Nota opcional..."
                className="w-full p-3 rounded-xl border border-[#E4EBE6] bg-[#F7F9F7] text-sm text-[#1E2F23] resize-none h-16 focus:outline-none focus:border-[#6B9080]" />
              <div className="flex gap-2">
                <button onClick={() => setShowMomentForm(false)}
                  className="flex-1 py-3 rounded-2xl border border-[#E4EBE6] text-[#2C4238] text-sm font-semibold">
                  Cancelar
                </button>
                <button onClick={addMoment} disabled={!selMoment}
                  className="flex-1 py-3 rounded-2xl bg-[#5B8FCC] text-white text-sm font-semibold disabled:opacity-40">
                  Guardar
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Evening ── */}
        <div className="border border-[#E4EBE6] rounded-2xl overflow-hidden">
          <button
            onClick={() => setShowEvening(o => !o)}
            className="w-full flex items-center justify-between px-4 py-3"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-[#EEF4F1] flex items-center justify-center">
                <Moon size={15} className="text-[#6B9080]" />
              </div>
              <div className="text-left">
                <p className="text-xs font-semibold text-[#1E2F23]">Check-in nocturno</p>
                {existingEvening
                  ? <p className="text-xs text-[#84A98C]">Completado · Riesgo {existingEvening.riskLevel}/10</p>
                  : <p className="text-xs text-[#2C4238]">Sin registro</p>
                }
              </div>
            </div>
            <span className="text-xs font-semibold text-[#6B9080]">
              {showEvening ? "Cerrar" : existingEvening ? "Editar" : "Añadir"}
            </span>
          </button>

          {showEvening && (
            <div className="px-4 pb-4 border-t border-[#F0F5F2] animate-fade-in pt-4 space-y-4">
              <div>
                <p className="text-xs font-semibold text-[#2C4238] uppercase tracking-wide mb-2">¿Cómo terminaste el día?</p>
                <div className="flex justify-between">
                  {MOODS.map((emoji, i) => (
                    <button key={i} onClick={() => setMood(i)}
                      className={`w-10 h-10 rounded-full text-xl flex items-center justify-center transition-all border-2 ${
                        mood === i ? "border-[#6B9080] bg-[#EEF4F1] scale-110" : "border-transparent bg-[#F7F9F7]"
                      }`}>
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <p className="text-xs font-semibold text-[#2C4238] uppercase tracking-wide">Nivel de riesgo</p>
                  <span className="text-sm font-bold" style={{ color: riskColor(riskLevel) }}>{riskLevel}/10</span>
                </div>
                <input type="range" min="1" max="10" value={riskLevel}
                  onChange={e => setRiskLevel(Number(e.target.value))} className="w-full accent-[#6B9080]" />
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <p className="text-xs font-semibold text-[#2C4238] uppercase tracking-wide">Ansiedad</p>
                  <span className="text-sm font-bold" style={{ color: riskColor(ansiedad * 2) }}>{ansiedad}/5</span>
                </div>
                <input type="range" min="1" max="5" value={ansiedad}
                  onChange={e => setAnsiedad(Number(e.target.value))} className="w-full accent-[#6B9080]" />
              </div>
              <div className="flex items-center justify-between bg-[#FFF9F7] rounded-xl px-4 py-3 border border-[#F4C5B5]">
                <p className="text-xs font-semibold text-[#8B5A48]">¿Hubo consumo hoy?</p>
                <button onClick={() => setConsumo(c => !c)}
                  className={`w-12 h-6 rounded-full transition-colors relative ${consumo ? "bg-[#E07A5F]" : "bg-[#E4EBE6]"}`}>
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${consumo ? "translate-x-7" : "translate-x-1"}`} />
                </button>
              </div>
              <div>
                <p className="text-xs font-semibold text-[#2C4238] uppercase tracking-wide mb-2">Triggers</p>
                <div className="flex flex-wrap gap-2">
                  {TRIGGERS.map(t => (
                    <button key={t} onClick={() => toggleTrigger(t)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        selTriggers.includes(t)
                          ? "bg-[#6B9080] text-white border-[#6B9080]"
                          : "bg-white text-[#2C4238] border-[#E4EBE6]"
                      }`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-[#2C4238] uppercase tracking-wide mb-1">Notas libres</p>
                <textarea value={notes} onChange={e => setNotes(e.target.value)}
                  placeholder="¿Qué aprendiste hoy?"
                  className="w-full p-3 rounded-xl border border-[#E4EBE6] bg-[#F7F9F7] text-sm text-[#1E2F23] resize-none h-20 focus:outline-none focus:border-[#6B9080]" />
              </div>
              <button onClick={saveEvening}
                className="w-full py-3 rounded-2xl bg-[#6B9080] text-white font-semibold text-sm">
                Guardar noche
              </button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
